import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from './ui/command';
import { useModelList } from '../lib/useModelList';

const MAX_RESULTS = 60;

export const ModelCombobox = ({ value, onChange, placeholder = 'Seleziona modello', testid, options }) => {
    const apiModels = useModelList();
    const list = useMemo(() => options || apiModels.map(m => m.name), [options, apiModels]);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const results = useMemo(() => {
        const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
        const base = terms.length
            ? list.filter(m => { const l = m.toLowerCase(); return terms.every(t => l.includes(t)); })
            : list;
        return base.slice(0, MAX_RESULTS);
    }, [query, list]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid={testid}>
                    <span className={cn('truncate', !value && 'text-muted-foreground')}>{value || placeholder}</span>
                    <span className="flex items-center shrink-0">
                        {value && (
                            <span
                                role="button"
                                aria-label="Svuota modello"
                                className="ml-2 inline-flex pointer-events-auto opacity-50 hover:opacity-100"
                                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange(''); }}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                data-testid={testid ? `${testid}-clear` : undefined}
                            >
                                <X className="h-4 w-4" />
                            </span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                    </span>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[420px]" align="end">
                <Command shouldFilter={false}>
                    <CommandInput placeholder="Cerca modello (es. SBA63 3P)..." value={query} onValueChange={setQuery} />
                    <CommandList>
                        <CommandEmpty>Nessun modello trovato</CommandEmpty>
                        <CommandGroup>
                            {value && !list.includes(value) && (
                                <CommandItem value={value} onSelect={() => setOpen(false)}>
                                    <Check className="mr-2 h-4 w-4 opacity-100" />
                                    <span className="truncate flex-1">{value}</span>
                                    <span className="ml-2 text-xs text-muted-foreground shrink-0">attuale</span>
                                </CommandItem>
                            )}
                            {results.map(m => (
                                <CommandItem
                                    key={m}
                                    value={m}
                                    onSelect={() => { onChange(m); setOpen(false); setQuery(''); }}
                                    data-testid={`model-option-${m.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                                >
                                    <Check className={cn('mr-2 h-4 w-4', value === m ? 'opacity-100' : 'opacity-0')} />
                                    <span className="truncate flex-1">{m}</span>
                                </CommandItem>
                            ))}
                            {results.length >= MAX_RESULTS && (
                                <div className="px-3 py-2 text-xs text-muted-foreground">Troppi risultati: affina la ricerca…</div>
                            )}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default ModelCombobox;
